import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  FileText, Mail, ReceiptPoundSterling, BadgeCheck, ShieldCheck, Truck,
  Package, CheckCircle2,
} from 'lucide-react';

/**
 * Shared orders tab header.
 *
 * Two modes:
 *   • Default (3 tabs)   — Drafts / Submitted / Completed. Used by ProEpoxy
 *     and ProDecor where the in-flight lifecycle is grouped into one bucket.
 *   • `granular` (6 tabs) — Drafts / Awaiting Invoice / Ready for Payment /
 *     Payment Made / Payment Received / Dispatched. Used by ProBound's
 *     installer-facing "My Orders" so each lifecycle stage is its own tab.
 *
 * Selected tab is synced to URL `?tab=…`. Parent reads `useOrdersTab()` or
 * the exported status-map to filter rows.
 */
export const ORDERS_TAB_STATUS = {
  drafts: ['draft'],
  live: ['submitted', 'pending', 'invoiced', 'paid', 'payment_confirmed', 'dispatched'],
  completed: ['delivered', 'cancelled'],
};

// Granular: each lifecycle stage gets its own tab. Note that `submitted` and
// the legacy alias `pending` share the "Awaiting Invoice" tab.
export const ORDERS_GRANULAR_TAB_STATUS = {
  drafts:            ['draft'],
  awaiting_invoice:  ['submitted', 'pending'],
  ready_for_payment: ['invoiced'],
  payment_made:      ['paid'],
  // ProBound uses `payment_confirmed`; ProEpoxy / ProDecor use the shorter
  // legacy alias `confirmed`. Both map to the same "Payment Received" bucket.
  payment_received:  ['payment_confirmed', 'confirmed'],
  dispatched:        ['dispatched', 'delivered'],
};

const TABS = [
  { key: 'drafts',    label: 'Drafts',    icon: FileText },
  { key: 'live',      label: 'Submitted', icon: Truck },
  { key: 'completed', label: 'Completed', icon: BadgeCheck },
];

const GRANULAR_TABS = [
  { key: 'drafts',            label: 'Drafts',                  icon: FileText,             shortLabel: 'Drafts' },
  { key: 'awaiting_invoice',  label: 'Awaiting Invoice from Ox', icon: Mail,                shortLabel: 'Awaiting Invoice' },
  { key: 'ready_for_payment', label: 'Ready for Payment',       icon: ReceiptPoundSterling, shortLabel: 'Ready to Pay' },
  { key: 'payment_made',      label: 'Payment Made',            icon: BadgeCheck,           shortLabel: 'Payment Made' },
  { key: 'payment_received',  label: 'Payment Received',        icon: ShieldCheck,          shortLabel: 'Payment Received' },
  { key: 'dispatched',        label: 'Order Dispatched',        icon: Package,              shortLabel: 'Dispatched' },
];

export function useOrdersTab(defaultKey = 'live') {
  const [sp, setSp] = useSearchParams();
  const active = sp.get('tab') || defaultKey;
  const setActive = (key) => {
    const next = new URLSearchParams(sp);
    next.set('tab', key);
    setSp(next, { replace: true });
  };
  return [active, setActive];
}

export default function OrdersTabsHeader({
  counts = {},
  showDrafts = true,
  accent = '#2F5E96',
  ink = '#fff',
  defaultKey,
  granular = false,
}) {
  const tabSet = granular ? GRANULAR_TABS : TABS;
  const [active, setActive] = useOrdersTab(defaultKey || (granular ? 'awaiting_invoice' : 'live'));
  const visible = tabSet.filter(t => showDrafts || t.key !== 'drafts');

  return (
    <div
      className="flex flex-wrap gap-1 p-1 rounded-xl bg-white border border-gray-200 shadow-sm"
      data-testid="orders-tabs-header"
    >
      {visible.map(tab => {
        const Icon = tab.icon;
        const isActive = active === tab.key;
        const count = counts[tab.key];
        const showCount = typeof count === 'number';
        return (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActive(tab.key)}
            data-testid={`orders-tab-${tab.key}`}
            title={tab.label}
            className={`flex items-center gap-1.5 px-2.5 sm:px-3.5 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all
              ${isActive ? 'shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`}
            style={isActive ? { background: accent, color: ink } : undefined}
          >
            <Icon className="w-4 h-4 shrink-0" />
            <span className={granular ? 'hidden md:inline' : ''}>{tab.label}</span>
            {granular && <span className="md:hidden">{tab.shortLabel}</span>}
            {showCount && count > 0 && (
              <span
                className={`ml-0.5 text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center ${
                  isActive ? 'bg-white/25' : 'bg-gray-100 text-gray-700'
                }`}
                data-testid={`orders-tab-${tab.key}-count`}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
